import { motion, Transition } from 'framer-motion';
import { FC } from 'react';

import { ChevronDownIcon } from '../../assets/icons/index';
import LoaderIcon from '../../assets/icons/loader-icon';
import { AppFormattedMessage } from '../app-formatted-message/index';
import Button from '../button/index';

const CHEVRON_TRANSITION: Transition = { duration: 0.22, ease: 'easeOut' };

export type LoadMoreButtonProps = {
  // Rows the next click reveals.
  count: number;
  // A page on its way from the server; a list revealed on the client never waits.
  isLoading?: boolean;
  onLoadMore: () => void;
  onShowLess: () => void;
  // Everything is shown: the same pill folds the list back, its chevron turned up.
  showLess: boolean;
};

/**
 * The "Load N more" / "Show less" pill under a long table, as the cap table and the data room use
 * it. Loading keeps the label and the pill's size; only the chevron gives way to the app's spinner.
 */
export const LoadMoreButton: FC<LoadMoreButtonProps> = ({
  count,
  isLoading = false,
  onLoadMore,
  onShowLess,
  showLess,
}) => (
  <Button
    aria-busy={isLoading}
    className="w-fit shrink-0 gap-1.5 rounded-[44px] border border-gray-200 bg-gray-25 px-3 py-1.5 text-sm font-[450] text-brand-700 shadow-lg disabled:cursor-default"
    disabled={isLoading}
    onClick={showLess ? onShowLess : onLoadMore}
    styleType="NONE"
  >
    {showLess ? (
      <AppFormattedMessage id="SHOW_LESS" />
    ) : (
      <AppFormattedMessage id="LOAD_X_MORE" values={{ count }} />
    )}
    {isLoading ? (
      <LoaderIcon className="size-3.5" height={14} width={14} />
    ) : (
      <motion.span
        animate={{ rotate: showLess ? 180 : 0 }}
        className="inline-flex"
        transition={CHEVRON_TRANSITION}
      >
        <ChevronDownIcon className="size-3.5" iconColor="#2565C8" />
      </motion.span>
    )}
  </Button>
);
